<script setup lang="ts">
import OptionsNavbar from './OptionsNavbar.vue';
import SchemesOld from './SchemesOld/index.vue';
import Sidebar from './Sidebar/index.vue';
import Schemes from './Schemes/index.vue';
import { useRiskStore  } from '@/stores/riskData'
import { storeToRefs } from 'pinia'
const store = useRiskStore()
const { productOutput, riskData } = storeToRefs(store)
defineOptions({ name: 'QuoteTab' });
import { UpdateQuote as updateQuoteAPI } from '@/composables/useQuoteAPIRequests'
import PromoCode from '@/components/Common/PromoCode.vue';
import AdminFees from './AdminFees.vue';
import SchemeSummary from '../SchemeSummary.vue';
import AtlasButton from '@/components/Common/AtlasButton.vue';
import Modal from '@/components/Common/Modal.vue';
import { useShowPromoCodeBox } from '@/components/Quote/composables/useShowPromoCodeBox'
import { useQuoteProductConfig } from '@/components/Quote/composables/useQuoteProductConfig'

import { ref, computed, toRaw, watch } from 'vue';
import StickySummaryBar from '../StickySummaryBar.vue';
import type { FeeBounds } from '@/utils/feeValidation';

const props = defineProps<{
    productId: string
    action: string
    quoteId: string
    allowAdminFeeEdit?: boolean
    showPrintResults?: boolean
    showSchemeTitles?: boolean
}>()

const { config: productConfig } = useQuoteProductConfig(computed(() => props.productId))

const showPromoCodeBox = useShowPromoCodeBox(computed(() => props.productId));

function handleSelect(id: string) {
  store.selectScheme(id);
  // The toggled scheme, not the first selected - find-first picks the wrong one once several are.
  const toggled = productOutput.value?.ReturnSchemes?.find((s: any) => s.SchemeId === id);
  onSchemeSelect(id, !!toggled?.IsSelected);
}

const emit = defineEmits<{
  (e: 'nav-back'): void 
  (e: 'nav-next'): void
  (e: 'enter-review'): void
}>();

function tabBack() { emit('nav-back') }
function tabNext() {
    // Block progression while the broker fee / commission is invalid.
    if (!feesValid.value) return;
    emit('nav-next')
}

async function onSchemeSelect(schemeId: string, isSchemeSelected: boolean) {
  const lists = false
  const page = 3
  const addonsFlag = false
  const toggled = productOutput.value?.ReturnSchemes?.find((s: any) => s.SchemeId === schemeId)
  const cover = toRaw(toggled)

  const { UpdateQuote } = await updateQuoteAPI(
    props.action,
    props.productId,
    props.quoteId,
    {
      schemeId,
      page,
      showReviewLists: lists,
      addonQuoteRequest: addonsFlag,
      transactionType: props.action,
      cover,
      selectOnly: true,
      singleInsurer: true,
      isSchemeSelected,
    }
  )

  // Makes sure Documents and AddOnDocuments are updated in the store when a scheme is selected/deselected.
  if (isSchemeSelected) {
    store.setPayload({
      ...(store.payload ?? {}),
      productOutput: {
        ...productOutput.value,
        Documents: UpdateQuote?.Documents ?? [],
        AddOnDocuments: UpdateQuote?.AddOnDocuments ?? [],
      },
    })
  }
}

const filteredKey = ref<(string | null)[]>([]);

const filteredSchemes = computed(() => {
  const schemes = productOutput.value?.ReturnSchemes.filter((returnScheme: any) => returnScheme.CreatedDate != '0001-01-01T00:00:00') ?? [];
    if (!filteredKey.value.length) return schemes;

    return schemes.filter((s:any) =>
        filteredKey.value.every(key => {
            if (!key) return true;
            return s.Features?.[key]?.IsIncluded;
        })
    );
});


const selectedScheme = computed(() => {
    return productOutput.value?.ReturnSchemes.find((s: any) => s.IsSelected) ?? null;
});


const adminFeeEditable = computed(() => riskData.value?.AdditionalFormData?.isCetaMembershipId === true);


const isOnAccountBroker = computed(() =>
    riskData.value?.AdditionalFormData?.membershipType === 'broker' &&
    riskData.value?.AdditionalFormData?.paymentType === 'OnAccount');


const adminFee = ref<string | number>('');
const brokerAdminFee = ref<string | number>('');
const isUpdating = ref(false);
const isApplyingFees = ref(false);

const ADMIN_FEE_BOUNDS: FeeBounds = { min: 0, max: 50, increment: 1 };

const commissionConfig = computed<any | null>(() => riskData.value?.AdditionalFormData?.commission ?? null);

const commissionBounds = computed<FeeBounds | null>(() => {
    const c = commissionConfig.value;
    if (!c) return null;
    const min = Number(c.minimum);
    const max = Number(c.maximum);

    // Negotiated bands step in fractions (ST&R is 17.5-35 in 2.5s), so the API's increment has to
    // be honoured - defaulting to 1 rejects every half-step value inside the band.
    const increment = Number(c.increment);
    return [min, max].every(Number.isFinite)
        ? { min, max, increment: Number.isFinite(increment) && increment > 0 ? increment : 1 }
        : null;
});

const policyCommissionRate = computed<number | null>(() => {
    const raw = commissionConfig.value?.policyCommissionRate;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
});

const commissionDefault = computed<number | null>(() => {
    const raw = commissionConfig.value?.default;
    if (raw === null || raw === undefined || raw === '') return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
});

const brokerFeeLocked = computed(() =>
    !adminFeeEditable.value && !isOnAccountBroker.value && policyCommissionRate.value !== null);

const activeFeeBounds = computed<FeeBounds | null>(() => {
    if (adminFeeEditable.value) return null;
    return isOnAccountBroker.value ? ADMIN_FEE_BOUNDS : commissionBounds.value;
});

const feesValid = ref(true);

watch(selectedScheme, (scheme: any) => {
    if (!scheme) return;
    adminFee.value = scheme.OverriddenAdminFee ?? '';
    if (isOnAccountBroker.value) {
        // On-account agents take the broker fee as an admin fee (£).
        brokerAdminFee.value = scheme.AgentAdminFee ?? '';
    } else {
        // Off-account commission (%). A policy commission rate wins outright; then
        // an existing returned value; then the configured default.
        brokerAdminFee.value = policyCommissionRate.value
            ?? scheme.AgentOverriddenCommissionPercentage
            ?? scheme.AgentCommissionPercentage
            ?? commissionDefault.value
            ?? '';
    }
}, { immediate: true });

async function applyAdminFees() {
    const scheme = selectedScheme.value;
    if (!scheme) return;
    // A policy commission rate is fixed and read-only, so there's nothing to apply.
    if (brokerFeeLocked.value) return;
    // Don't push an out-of-range/invalid fee to the API; the field shows the error.
    if (!feesValid.value) return;

    const newAdminFee = Number(adminFee.value) || 0;
    const newBrokerFee = Number(brokerAdminFee.value) || 0;
    // On-account writes the agent admin fee (£); off-account writes the commission
    // override percentage (%). AgentCommissionPremium is computed server-side from
    // this percentage.
    const brokerFeeField = isOnAccountBroker.value ? 'AgentAdminFee' : 'AgentOverriddenCommissionPercentage';
    const currentBrokerFee = isOnAccountBroker.value
        ? Number(scheme.AgentAdminFee) || 0
        : Number(scheme.AgentOverriddenCommissionPercentage ?? scheme.AgentCommissionPercentage) || 0;

    if (newAdminFee === (Number(scheme.OverriddenAdminFee) || 0) &&
        newBrokerFee === currentBrokerFee) {
        return;
    }

    scheme.OverriddenAdminFee = newAdminFee;
    scheme[brokerFeeField] = newBrokerFee;

    isApplyingFees.value = true;
    try {
        const { UpdateQuote } = await updateQuoteAPI(
            props.action,
            props.productId,
            props.quoteId,
            {
                schemeId: scheme.SchemeId,
                page: 3,
                showReviewLists: false,
                addonQuoteRequest: false,
                transactionType: props.action,
                cover: toRaw(scheme),
                selectOnly: true,
                singleInsurer: true,
            }
        );

        if (UpdateQuote?.ReturnSchemes) {
            store.setPayload({ ...(store.payload ?? {}), productOutput: UpdateQuote });
        }
    } finally {
        isApplyingFees.value = false;
    }
}

const showNext = computed(() => {
    const stateCheck =
     (productOutput.value?.IsReferred || productOutput.value?.IsDeclined) &&
        (props.action ?? '').toLowerCase().includes('review');

    // TEMP: allow progression when no schemes have been quoted yet (dev/testing
    // before the real quote flow is wired up). Remove once productOutput is
    // populated end-to-end.
    const noSchemesYet = !productOutput.value?.ReturnSchemes?.length;

    const finalCheck = (selectedScheme.value !== null || noSchemesYet) && !stateCheck;
    return finalCheck;
});

// Referred/declined modal — shown when a non-review quote comes back referred or declined.
const showReferredModal = ref(false)
const isReviewAction = computed(() => (props.action ?? '').toLowerCase().includes('review'))

// The quote-level IsReferred/IsDeclined flags are a roll-up across the whole product
// output; they can be true even when individual schemes in ReturnSchemes are still
// quotable. Only force the referral modal when there is nothing bindable on the page —
// otherwise the user is looking at available schemes and the modal is misleading.
const hasAvailableSchemes = computed(() =>
    filteredSchemes.value.some((s: any) => !s.IsReferred && !s.IsDeclined)
)

watch(
    () => [productOutput.value?.IsReferred, productOutput.value?.IsDeclined, hasAvailableSchemes.value] as const,
    ([referred, declined, hasAvailable]) => {
        if ((referred || declined) && !isReviewAction.value && !hasAvailable) showReferredModal.value = true
    },
    { immediate: true }
)
function leaveToCustomer() {
    window.location.href = '/'
}

// Enter review in-place: flip review mode on, snapshot the current risk data as the
// original (shadow), then jump to the first question tab where the boxes render.
function enterReview() {
    store.isReviewMode = true
    store.ensureShadow(store.riskData)
    showReferredModal.value = false
    emit('enter-review')
}


</script>

<template>
    <StickySummaryBar :selectedScheme="selectedScheme" :action="props.action">
        <template #summary-bar-content>
            <!-- Top quote tools -->
            <div class="row mt-3 mb-3 justify-content-between">
                <div class="col-lg-3 mb-2 mb-lg-0 d-flex" v-if="showPromoCodeBox">
                    <PromoCode :productId="props.productId" />
                </div>
                <div class="col-lg-4 mb-2 mb-lg-0 d-flex">
                    <AdminFees v-model:admin-fee="adminFee" v-model:broker-admin-fee="brokerAdminFee" v-model:valid="feesValid" :selectedScheme="selectedScheme" :adminFeeEditable="adminFeeEditable" :allowAdminFeeEdit="props.allowAdminFeeEdit" :isOnAccount="isOnAccountBroker" :bounds="activeFeeBounds" :locked="brokerFeeLocked" @apply="applyAdminFees" />
                </div>
                <div class="col-lg-5 d-flex" v-if="selectedScheme">
                    <SchemeSummary :scheme="selectedScheme" :action="props.action" :loading="isApplyingFees" @nav-next="tabNext" />
                </div>
            </div>

            <!-- Filters row -->
        </template>
        <template #summary-bar-actions>
            <AtlasButton
                v-if="selectedScheme"
                variant="primary"
                class="ml-3"
                :disabled="!feesValid"
                @click="tabNext"
            >
                Proceed
            </AtlasButton>
        </template>
    </StickySummaryBar>

    <!-- Original content below - only mount when productOutput data exists -->
    <template v-if="productOutput">
        <!-- Below is the old filter navbar component for reference -->
        <!-- <OptionsNavbar @nav-back="tabBack" @nav-next="tabNext" @filter-selected="addFilter" :showNext="showNext" /> -->

        <Schemes
            :schemes="filteredSchemes"
            :loading="isUpdating"
            @select="handleSelect"
            :product-id="props.productId"
            :quote-id="props.quoteId"
            :action="props.action"
            :showPrintResults="props.showPrintResults"
            :showSchemeTitles="props.showSchemeTitles"
        />
        <!-- Below is the old schemes component for reference -->
         <!-- <div class="row">
            <SchemesOld
            @select="handleSelect"
            :filteredSchemes="filteredSchemes"
            :productId="props.productId"
            :action="props.action"
            :quoteId="props.quoteId"
            />
            <Sidebar :filteredSchemes="filteredSchemes" :productId="props.productId" />
         </div> -->

        <hr />
        <div class="d-flex justify-content-between">
            <button class="btn btn-outline-primary" id="quoteBackBtn"  @click="tabBack"><em class="far fa-chevron-left mR-5"></em> Back</button>
            <span>
                <!-- ko if: hasAtLeastOneScheme -->
                <!-- ko if: showButtonPurchase && !($root.Action.toLowerCase().includes('review') && (IsReferred() || IsDeclined())) -->
                <button v-if="showNext" :disabled="!feesValid" class="btn btn-outline-primary" id="quoteNextBtn" @click="tabNext">Next <em class="far fa-chevron-right mL-5"></em></button>
                <!-- /ko -->
                <!-- /ko -->
            </span>
        </div>
    </template>

    <!-- Referred / declined Modal.
         tempOperatorScriptVue renders the Modal's backdrop with Bootstrap's `fade show`
         classes, which is what makes the backdrop translucent instead of a solid #000. -->
    <Modal :modelValue="showReferredModal"
            modalName="startOverModalLabel"
            :tempOperatorScriptVue="true"
            @close="showReferredModal = false">
        <template #header>
            <h5 class="modal-title" id="startOverModalLabel">Your quote has been referred</h5>
        </template>

        <template #body>
            <div class="row">
                <div class="col-sm-12">
                    <p>You can either review the questions filled or you can leave and the quote will be picked up by an underwriter</p>
                </div>
            </div>
        </template>

        <template #footer>
            <button type="button" class="btn btn-outline-secondary" @click="leaveToCustomer"><em class="far fa-chevron-left mR-5"></em> Leave</button>
            <button type="button" class="btn btn-outline-primary" @click="enterReview"><em class="far fa-arrow-rotate-back mR-5"></em>Review data</button>
        </template>
    </Modal>

    <!-- THE BELOW NEEDS LOOKING INTO BUT I THINK ITS OLD LOGIC THAT IS NO LONGER USED -->
    <div class="modal fade" id="compareSchemesModal" tabindex="-1" role="dialog" aria-labelledby="compareSchemesModal" aria-hidden="true">
        <div class="modal-dialog modal-lg" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">Compare Schemes</h5>
                </div>
                <div class="modal-body p-2">
                    <table class="table table-bordered table-striped table-sm">
                        <tr>
                            <td class="align-middle" style="width: 125px"></td>
                            <!-- ko foreach : { data: ProductOutput().ReturnSchemes, as : 'scheme' } -->
                            <!-- ko if: $root.SchemeData().SchemesToCompare().indexOf(scheme.SchemeDescription()) != -1 -->
                            <td class="align-middle p-7"><img loading="lazy" width="75px" class="mx-auto proposalImg p-0 d-block" id="insurer-logo" data-bind="attr : { 'src' : 'https://isistech.blob.core.windows.net/cdn/resources/CETA/scheme-images/' + scheme.SchemeDescription() + '.png' }" /></td>
                            <!-- /ko -->
                            <!-- /ko -->
                        </tr>

                        <tr>
                            <td class="align-middle">Insurer</td>
                            <!-- ko foreach : { data: ProductOutput().ReturnSchemes, as : 'scheme' } -->
                            <!-- ko if: $root.SchemeData().SchemesToCompare().indexOf(scheme.SchemeDescription()) != -1 -->
                            <td class="align-middle text-center" data-bind="text: scheme.Insurer().Name"></td>
                            <!-- /ko -->
                            <!-- /ko -->
                        </tr>

                        <tr>
                            <td class="align-middle">Accidental Damage</td>
                            <!-- ko foreach : { data: ProductOutput().ReturnSchemes, as : 'scheme' } -->
                            <!-- ko if: $root.SchemeData().SchemesToCompare().indexOf(scheme.SchemeDescription()) != -1 -->
                            <td class="align-middle">
                                <!-- ko if: scheme.Features().accidentaldamagecover.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-check text-success"></i>
                                <!-- /ko -->
                                <!-- ko if: !scheme.Features().accidentaldamagecover.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-times"></i>
                                <!-- /ko -->
                            </td>
                            <!-- /ko -->
                            <!-- /ko -->
                        </tr>

                        <tr>
                            <td class="align-middle">Legal Cover</td>
                            <!-- ko foreach : { data: ProductOutput().ReturnSchemes, as : 'scheme' } -->
                            <!-- ko if: $root.SchemeData().SchemesToCompare().indexOf(scheme.SchemeDescription()) != -1 -->
                            <td class="align-middle">
                                <!-- ko if: scheme.Features().legalcover.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-check text-success"></i>
                                <!-- /ko -->
                                <!-- ko if: !scheme.Features().legalcover.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-times"></i>
                                <!-- /ko -->
                            </td>
                            <!-- /ko -->
                            <!-- /ko -->
                        </tr>

                        <tr>
                            <td class="align-middle">Europe Cover</td>
                            <!-- ko foreach : { data: ProductOutput().ReturnSchemes, as : 'scheme' } -->
                            <!-- ko if: $root.SchemeData().SchemesToCompare().indexOf(scheme.SchemeDescription()) != -1 -->
                            <td class="align-middle">
                                <!-- ko if: scheme.Features().europeancover.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-check text-success"></i>
                                <!-- /ko -->
                                <!-- ko if: !scheme.Features().europeancover.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-times"></i>
                                <!-- /ko -->
                            </td>
                            <!-- /ko -->
                            <!-- /ko -->
                        </tr>

                        <tr>
                            <td class="align-middle">Family Cover</td>
                            <!-- ko foreach : { data: ProductOutput().ReturnSchemes, as : 'scheme' } -->
                            <!-- ko if: $root.SchemeData().SchemesToCompare().indexOf(scheme.SchemeDescription()) != -1 -->
                            <td class="align-middle">
                                <!-- ko if: scheme.Features().familycover.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-check text-success"></i>
                                <!-- /ko -->
                                <!-- ko if: !scheme.Features().familycover.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-times"></i>
                                <!-- /ko -->
                            </td><!-- /ko -->
                            <!-- /ko -->
                        </tr>

                        <tr>
                            <td class="align-middle">Fire & Theft</td>
                            <!-- ko foreach : { data: ProductOutput().ReturnSchemes, as : 'scheme' } -->
                            <!-- ko if: $root.SchemeData().SchemesToCompare().indexOf(scheme.SchemeDescription()) != -1 -->
                            <td class="align-middle">
                                <!-- ko if: scheme.Features().fireandtheft.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-check text-success"></i>
                                <!-- /ko -->
                                <!-- ko if: !scheme.Features().fireandtheft.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-times"></i>
                                <!-- /ko -->
                            </td><!-- /ko -->
                            <!-- /ko -->
                        </tr>

                        <tr>
                            <td class="align-middle">Storm & Flood</td>
                            <!-- ko foreach : { data: ProductOutput().ReturnSchemes, as : 'scheme' } -->
                            <!-- ko if: $root.SchemeData().SchemesToCompare().indexOf(scheme.SchemeDescription()) != -1 -->
                            <td class="align-middle">
                                <!-- ko if: scheme.Features().stormandfloodcover.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-check text-success"></i>
                                <!-- /ko -->
                                <!-- ko if: !scheme.Features().stormandfloodcover.IsIncluded -->
                                <i class="feat-icon d-block mx-auto far fa-times"></i>
                                <!-- /ko -->
                            </td><!-- /ko -->
                            <!-- /ko -->
                        </tr>
                        <tr>
                            <td class="align-middle">Excess</td>
                            <!-- ko foreach : { data: ProductOutput().ReturnSchemes, as : 'scheme' } -->
                            <!-- ko if: $root.SchemeData().SchemesToCompare().indexOf(scheme.SchemeDescription()) != -1 -->
                            <td class="align-middle text-center">
                                £<span data-bind="text: scheme.Covers()[0].Excesses()[0].Amount"></span>
                            </td>
                            <!-- /ko -->
                            <!-- /ko -->
                        </tr>
                        <tr>
                            <td class="align-middle">Cover</td>
                            <!-- ko foreach : { data: ProductOutput().ReturnSchemes, as : 'scheme' } -->
                            <!-- ko if: $root.SchemeData().SchemesToCompare().indexOf(scheme.SchemeDescription()) != -1 -->
                            <td class="align-middle text-center">
                                <span data-bind="text: scheme.Features().typeofcover.Value"></span>
                            </td>
                            <!-- /ko -->
                            <!-- /ko -->
                        </tr>
                    </table>
                </div>
            </div>
        </div>
    </div>
</template>

<style>
    .fa-times:not(.details) {
        color: #bdbdbd;
    }

    .fa-check {
        color: rgb(76, 175, 80);
    }

    .feat-icon {
        font-size: 18px;
        height: 26px;
        width: 26px !important;
    }

    .feature {
        width: 94px;
    }

    label.compare-icon input[type=checkbox]:checked + .compare-checkbox-icon {
        color: #4caf50;
    }
</style>
